/**
 * Yönetici paneli: Özet / Kullanıcılar / Referans Kodları.
 *
 * Yalnızca `is_admin=true` oturumlarda gösterilir (bkz. App.tsx) - bu
 * yalnızca GÖRÜNÜRLÜK katmanıdır; gerçek yetki zorlaması backend'de
 * `require_admin` ile yapılır (bkz. api/auth_deps.py). Ödeme sistemi
 * veya referans kodu dışında bir üyelik mekanizması YOKTUR (kullanıcı
 * isteği).
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import type { AdminDashboard, AdminUser, ReferralCode } from "../api/types";
import { dateTimeWithYear } from "../lib/format";
import { Card, Empty, IconRefresh, Kpi, Pill, Row } from "./Ui";

type AdminTab = "dashboard" | "users" | "codes" | "announcement" | "pinned-note";

export function AdminPanel({
  notify,
}: {
  notify: (message: string, tone: "ok" | "warn" | "error") => void;
}) {
  const [tab, setTab] = useState<AdminTab>("dashboard");
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [codes, setCodes] = useState<ReferralCode[]>([]);
  const [announcementDraft, setAnnouncementDraft] = useState("");
  const [pinnedNoteDraft, setPinnedNoteDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  /** En son uretilen gecici sifre. YALNIZCA bellekte durur; sayfa
   *  yenilenirse kaybolur ve yeniden sifirlamak gerekir - okunabilir bir
   *  sifrenin ortalikta kalmasi istenmez (bkz. backend PasswordResetOut). */
  const [resetResult, setResetResult] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const describe = useCallback(
    (error: unknown, fallback: string) => {
      notify(error instanceof ApiError ? error.message : fallback, "error");
    },
    [notify],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextDashboard, nextUsers, nextCodes, nextAnnouncement, nextPinnedNote] =
        await Promise.all([
          api.admin.dashboard(),
          api.admin.users(),
          api.admin.referralCodes(),
          api.announcement(),
          api.pinnedNote(),
        ]);
      setDashboard(nextDashboard);
      setUsers(nextUsers);
      setCodes(nextCodes);
      setAnnouncementDraft(nextAnnouncement.text);
      setPinnedNoteDraft(nextPinnedNote.text);
    } catch (error) {
      describe(error, "Yönetim verisi alınamadı");
    } finally {
      setLoading(false);
    }
  }, [describe]);

  useEffect(() => {
    void load();
  }, [load]);

  const createCode = useCallback(async () => {
    setBusy(true);
    try {
      await api.admin.createReferralCode();
      await load();
    } catch (error) {
      describe(error, "Kod oluşturulamadı");
    } finally {
      setBusy(false);
    }
  }, [describe, load]);

  const cancelCode = useCallback(
    async (code: string) => {
      setBusy(true);
      try {
        await api.admin.cancelReferralCode(code);
        await load();
      } catch (error) {
        describe(error, "Kod iptal edilemedi");
      } finally {
        setBusy(false);
      }
    },
    [describe, load],
  );

  const toggleSuspend = useCallback(
    async (user: AdminUser) => {
      setBusy(true);
      try {
        if (user.status === "SUSPENDED") {
          await api.admin.reactivateUser(user.id);
        } else {
          await api.admin.suspendUser(user.id);
        }
        await load();
      } catch (error) {
        describe(error, "Durum güncellenemedi");
      } finally {
        setBusy(false);
      }
    },
    [describe, load],
  );

  const resetPassword = useCallback(
    async (user: AdminUser) => {
      setBusy(true);
      try {
        const result = await api.admin.resetUserPassword(user.id);
        setResetResult({ email: result.email, password: result.temporary_password });
        await load();
      } catch (error) {
        describe(error, "Şifre sıfırlanamadı");
      } finally {
        setBusy(false);
      }
    },
    [describe, load],
  );

  const saveAnnouncement = useCallback(async () => {
    setBusy(true);
    try {
      const result = await api.admin.updateAnnouncement(announcementDraft.trim());
      setAnnouncementDraft(result.text);
      notify(result.text ? "Duyuru güncellendi" : "Duyuru kaldırıldı", "ok");
    } catch (error) {
      describe(error, "Duyuru güncellenemedi");
    } finally {
      setBusy(false);
    }
  }, [announcementDraft, describe, notify]);

  const savePinnedNote = useCallback(async () => {
    setBusy(true);
    try {
      const result = await api.admin.updatePinnedNote(pinnedNoteDraft.trim());
      setPinnedNoteDraft(result.text);
      notify(result.text ? "Sabit bilgi güncellendi" : "Sabit bilgi kaldırıldı", "ok");
    } catch (error) {
      describe(error, "Sabit bilgi güncellenemedi");
    } finally {
      setBusy(false);
    }
  }, [pinnedNoteDraft, describe, notify]);

  const purgeUnused = useCallback(async () => {
    if (!window.confirm("Kullanılmamış tüm referans kodları silinsin mi? (Kullanılmış kodlara dokunulmaz)")) {
      return;
    }
    setBusy(true);
    try {
      const result = await api.admin.purgeUnusedReferralCodes();
      notify(`${result.removed} kullanılmamış kod temizlendi`, "ok");
      await load();
    } catch (error) {
      describe(error, "Kodlar temizlenemedi");
    } finally {
      setBusy(false);
    }
  }, [describe, load, notify]);

  const subTabs: { key: AdminTab; label: string }[] = [
    { key: "dashboard", label: "Özet" },
    { key: "users", label: "Kullanıcılar" },
    { key: "codes", label: "Referans Kodları" },
    { key: "announcement", label: "Duyuru" },
    { key: "pinned-note", label: "Sabit Bilgi" },
  ];

  return (
    <>
      <div className="title-row">
        <h2 className="section-title">Yönetim</h2>
        <button
          type="button"
          className="icon-btn"
          onClick={() => void load()}
          aria-label="Yenile"
          disabled={loading}
        >
          <IconRefresh spinning={loading} />
        </button>
      </div>

      <div className="btn-row admin-subtabs">
        {subTabs.map((item) => (
          <button
            key={item.key}
            type="button"
            className={tab === item.key ? "btn btn-primary" : "btn"}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" ? (
        <div className="kpi-grid">
          <Kpi label="Toplam Üye" value={String(dashboard?.total_users ?? 0)} />
          <Kpi label="Aktif" value={String(dashboard?.active_users ?? 0)} tone="up" />
          <Kpi label="Süresi Dolmuş" value={String(dashboard?.expired_users ?? 0)} tone="down" />
          <Kpi label="Askıda" value={String(dashboard?.suspended_users ?? 0)} tone="warn" />
          <Kpi label="Kullanılmamış Kod" value={String(dashboard?.unused_codes ?? 0)} tone="info" />
          <Kpi label="Kullanılmış Kod" value={String(dashboard?.used_codes ?? 0)} />
        </div>
      ) : null}

      {tab === "users" && resetResult ? (
        <Card title="Geçici şifre üretildi">
          <p className="note">
            <strong>{resetResult.email}</strong> için tek kullanımlık şifre. Üyeye ilet; bu
            şifreyle girdiğinde kendi şifresini belirlemeden uygulamayı göremez.
          </p>
          <p className="temp-password">{resetResult.password}</p>
          <p className="note">
            Bu şifre bir daha gösterilemez — saklanmıyor, yalnızca özeti tutuluyor. Kaybedersen
            yeniden sıfırla.
          </p>
          <div className="btn-row">
            <button
              type="button"
              className="btn"
              onClick={() => {
                void navigator.clipboard?.writeText(resetResult.password);
                notify("Geçici şifre kopyalandı", "ok");
              }}
            >
              Kopyala
            </button>
            <button type="button" className="btn" onClick={() => setResetResult(null)}>
              Kapat
            </button>
          </div>
        </Card>
      ) : null}

      {tab === "users" ? (
        users.length === 0 ? (
          <Empty title="Henüz kullanıcı yok" />
        ) : (
          <div className="rows">
            {users.map((user) => (
              <Card key={user.id}>
                <div className="rows">
                  <Row label="Ad Soyad">
                    {user.first_name} {user.last_name}
                  </Row>
                  <Row label="E-posta">{user.email}</Row>
                  <Row label="Telefon">{user.phone}</Row>
                  <Row label="Referans Kodu">{user.referral_code ?? "—"}</Row>
                  <Row label="Üyelik Başlangıcı">
                    {user.membership_start == null ? "Sınırsız" : dateTimeWithYear(user.membership_start)}
                  </Row>
                  <Row label="Üyelik Bitişi">
                    {user.membership_end == null ? "Sınırsız" : dateTimeWithYear(user.membership_end)}
                  </Row>
                  <Row label="Kalan Gün">
                    {user.remaining_days == null ? "Sınırsız" : user.remaining_days}
                  </Row>
                  <Row label="Durum">
                    <Pill
                      tone={
                        user.status === "ACTIVE" ? "up" : user.status === "SUSPENDED" ? "warn" : "down"
                      }
                    >
                      {user.status}
                    </Pill>
                    {user.is_admin ? (
                      <Pill tone="info" title="Yönetici">
                        ADMIN
                      </Pill>
                    ) : null}
                    {user.must_change_password ? (
                      <Pill
                        tone="warn"
                        title="Şifresi sıfırlandı; üye henüz kendi şifresini belirlemedi"
                      >
                        Geçici şifre
                      </Pill>
                    ) : null}
                  </Row>
                </div>
                {!user.is_admin ? (
                  <div className="btn-row" style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className={user.status === "SUSPENDED" ? "btn btn-primary" : "btn btn-danger"}
                      disabled={busy}
                      onClick={() => void toggleSuspend(user)}
                    >
                      {user.status === "SUSPENDED" ? "Askıyı Kaldır" : "Askıya Al"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      title="Tek kullanımlık geçici şifre üretir; üyenin açık oturumları kapanır"
                      onClick={() => void resetPassword(user)}
                    >
                      Şifre Sıfırla
                    </button>
                  </div>
                ) : null}
              </Card>
            ))}
          </div>
        )
      ) : null}

      {tab === "codes" ? (
        <>
          <div className="btn-row" style={{ marginBottom: 10 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void createCode()}
            >
              Yeni Referans Kodu Üret
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void purgeUnused()}>
              Kullanılmamışları Temizle
            </button>
          </div>
          {codes.length === 0 ? (
            <Empty title="Henüz referans kodu yok" />
          ) : (
            <div className="rows">
              {codes.map((code) => (
                <Card key={code.code}>
                  <div className="rows">
                    <Row label="Kod">
                      <span className="row-value" style={{ fontWeight: 700 }}>
                        {code.code}
                      </span>
                    </Row>
                    <Row label="Durum">
                      <Pill tone={code.status === "UNUSED" ? "info" : "neutral"}>{code.status}</Pill>
                    </Row>
                    <Row label="Oluşturulma">{dateTimeWithYear(code.created_at)}</Row>
                    {code.used_at ? <Row label="Kullanılma">{dateTimeWithYear(code.used_at)}</Row> : null}
                    {code.used_by_email ? <Row label="Kullanan">{code.used_by_email}</Row> : null}
                  </div>
                  {code.status === "UNUSED" ? (
                    <div className="btn-row" style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={busy}
                        onClick={() => void cancelCode(code.code)}
                      >
                        İptal Et
                      </button>
                    </div>
                  ) : null}
                </Card>
              ))}
            </div>
          )}
        </>
      ) : null}

      {tab === "announcement" ? (
        <Card title="Duyuru">
          <p className="note" style={{ marginBottom: 10 }}>
            Tüm aktif üyelerin "Duyurular" sekmesinde göreceği kısa bir mesaj. Boş bırakıp
            kaydetmek duyuruyu kaldırır.
          </p>
          <textarea
            className="input"
            style={{ width: "100%", maxWidth: "none", resize: "vertical" }}
            rows={4}
            value={announcementDraft}
            onChange={(event) => setAnnouncementDraft(event.target.value)}
            placeholder="Örn. Bakım çalışması: yarın 02:00-03:00 arası kısa kesinti olabilir."
          />
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void saveAnnouncement()}
            >
              Kaydet
            </button>
          </div>
        </Card>
      ) : null}

      {tab === "pinned-note" ? (
        <Card title="Sabit Bilgi">
          <p className="note" style={{ marginBottom: 10 }}>
            Duyuru'dan AYRI, nadiren değişen ve her zaman görünen kalıcı bir not (ör. destek
            hattı, iletişim bilgisi). Boş bırakıp kaydetmek kaldırır.
          </p>
          <textarea
            className="input"
            style={{ width: "100%", maxWidth: "none", resize: "vertical" }}
            rows={4}
            value={pinnedNoteDraft}
            onChange={(event) => setPinnedNoteDraft(event.target.value)}
            placeholder="Örn. Destek: +90 555 000 00 00"
          />
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void savePinnedNote()}
            >
              Kaydet
            </button>
          </div>
        </Card>
      ) : null}
    </>
  );
}
